/**
 * Kundali RESULT copy for the in-language regional editions: the section
 * titles, the chart, planet table, Moon/Lagna summary, dasha and dosha
 * sections. The terms themselves (graha, rashi, nakshatra, dosha names) come
 * from `@/lib/jyotish-regional-terms`; this file is the sentences around them.
 *
 * Each component keeps its existing en/hi copy and reads this only when its
 * `language` is a regional edition, so en and hi output is unchanged.
 *
 * Still English on a regional result, by design: the AI reading
 * (`result.interpretation`), dosha descriptions and remedy reasons (server
 * text), the yoga section, the report preview, the offer, the chat and the
 * share / feedback / contact blocks. Those either come from the backend in en/hi
 * or sit on en/hi commerce contracts.
 */
import { kundaliRegionalLocale, type KundaliRegionalLocale } from "@/i18n/regional-locales";

interface RegionalResultLabels {
  birthChart: string;
  planetaryPositions: string;
  doshaAnalysis: string;
  yogaAnalysis: string;
  yourReading: string;
  recommendedForYou: string;
  moonSign: string;
  nakshatra: string;
  ascendant: string;
  doshasDetected: string;
  severity: string;
  recommended: string;
  shopNow: string;
  shareYourKundali: string;
  shareDescription: string;
  shareOnWhatsApp: string;
  copyLink: string;
  copied: string;
  newReading: string;
  askAboutChart: string;
  doshaWord: string;
  doshaWordPlural: string;
  kundali: string;
}

export interface RegionalResultCopy {
  labels: RegionalResultLabels;
  title: (name: string) => string;
  computedFor: string;
  timeNotSet: string;
  chartStyle: { north: string; south: string };
  southChart: { label: string; centerTitle: string; centerSubtitle: string };
  chartMethodNote: string;
  chartNeedsTime: string;
  doshaNeedsTime: string;
  /** "Mars in house 7", for the dosha that graha triggers. */
  doshaPlacement: (graha: string, house: number | string) => string;
  summary: {
    needsBirthTime: string;
    moonChangesRashi: string;
    lagnaChanges: string;
    nakshatraChanges: string;
    pada: string;
  };
  /** The "born before HH:MM" lines of a no-time chart whose Moon changes sign or nakshatra. */
  windows: {
    before: (time: string) => string;
    after: (time: string) => string;
    between: (start: string, end: string) => string;
  };
  table: {
    title: string;
    subtitle: string;
    swipe: string;
    columns: readonly [string, string, string, string, string, string];
    padaPrefix: string;
    retrograde: string;
    exalted: string;
    debilitated: string;
    combust: string;
    pushkaraBhaga: string;
    pushkaraD9: string;
    pushkaraNoteTitle: string;
    pushkaraNote: string;
    changesSignToday: string;
    dayOnlyNote: string;
  };
  dasha: {
    vimshottari: string;
    heading: string;
    intro: string;
    engine: string;
    activeNow: string;
    birthLord: (lord: string) => string;
    orderPrefix: string;
    datesHidden: string;
    needsTime: string;
    rectify: string;
    mahadasha: (lord: string) => string;
    antardasha: (lord: string) => string;
  };
  slider: {
    ariaLabel: string;
    title: string;
    subtitle: string;
    active: string;
    years: string;
    mahadashaHeading: (lord: string, years: number) => string;
    antardashaHeading: string;
    now: string;
  };
}

export const REGIONAL_RESULT_COPY: Record<KundaliRegionalLocale, RegionalResultCopy> = {
  bn: {
    labels: {
      birthChart: "জন্ম কুষ্ঠি",
      planetaryPositions: "গ্রহের অবস্থান",
      doshaAnalysis: "দোষ বিশ্লেষণ",
      yogaAnalysis: "যোগ বিশ্লেষণ",
      yourReading: "আপনার ফলাদেশ",
      recommendedForYou: "আপনার জন্য প্রস্তাবিত",
      moonSign: "চন্দ্র রাশি",
      nakshatra: "নক্ষত্র",
      ascendant: "লগ্ন",
      doshasDetected: "পাওয়া গেছে",
      severity: "তীব্রতা",
      recommended: "প্রস্তাবিত",
      shopNow: "এখনই কিনুন",
      shareYourKundali: "আপনার কুষ্ঠি শেয়ার করুন",
      shareDescription: "বন্ধু ও পরিবারের সঙ্গে আপনার ফলাদেশ শেয়ার করুন",
      shareOnWhatsApp: "WhatsApp-এ শেয়ার করুন",
      copyLink: "লিঙ্ক কপি করুন",
      copied: "কপি হয়েছে!",
      newReading: "নতুন কুষ্ঠি",
      askAboutChart: "আপনার কুষ্ঠি নিয়ে প্রশ্ন করুন",
      doshaWord: "দোষ",
      doshaWordPlural: "দোষ",
      kundali: "জন্ম কুষ্ঠি",
    },
    title: (name) => `${name}-এর জন্ম কুষ্ঠি`,
    computedFor: "গণনা: ",
    timeNotSet: "সময় দেওয়া হয়নি",
    chartStyle: { north: "উত্তর ভারতীয় (ডায়মন্ড)", south: "দক্ষিণ ভারতীয় (চৌকো)" },
    southChart: {
      label: "দক্ষিণ ভারতীয় কুষ্ঠি ছক",
      centerTitle: "দক্ষিণ ভারতীয় ছক",
      centerSubtitle: "স্থির রাশিচক্র",
    },
    chartMethodNote:
      "লাহিড়ী অয়নাংশ (নিরয়ন/sidereal) ও সম্পূর্ণ রাশি (Whole-Sign) ভাব পদ্ধতিতে গণনা — শাস্ত্রীয় বৈদিক (পরাশরী) পদ্ধতি, যেখানে লগ্নের পুরো রাশিই প্রথম ভাব। পাশ্চাত্য (সায়ন/tropical) ছকের সঙ্গে গ্রহের রাশি স্বাভাবিকভাবেই আলাদা হবে।",
    chartNeedsTime:
      "কুষ্ঠি ছকে গ্রহগুলিকে লগ্ন থেকে গোনা ভাবে বসানো হয়, আর লগ্নের জন্য জন্ম সময় দরকার। সারা দিন একই থাকা গ্রহের রাশিগুলি নিচের সারণিতে আছে।",
    doshaNeedsTime:
      "দোষ যাচাইয়ের জন্য জন্ম সময় দরকার, কারণ এই যাচাই লগ্ন থেকে গোনা ভাব ব্যবহার করে। জন্ম সময় ছাড়া আমরা কোনো দোষকে আপনার বলে জানাই না।",
    doshaPlacement: (graha, house) => `${graha} ${house} নম্বর ভাবে`,
    summary: {
      needsBirthTime: "জন্ম সময় দরকার",
      moonChangesRashi: "আপনার জন্ম তারিখে চন্দ্র রাশি বদলায়",
      lagnaChanges: "প্রায় প্রতি 2 ঘণ্টায় বদলায়",
      nakshatraChanges: "এই তারিখে বদলায়",
      pada: "পাদ",
    },
    windows: {
      before: (time) => `${time}-এর আগে জন্ম`,
      after: (time) => `${time} বা তার পরে জন্ম`,
      between: (start, end) => `${start}–${end}-এর মধ্যে জন্ম`,
    },
    table: {
      title: "গ্রহের অবস্থান",
      subtitle: "Planetary Positions",
      swipe: "সরান",
      columns: ["গ্রহ", "রাশি", "অংশ", "নক্ষত্র", "ভাব", "অবস্থা"],
      padaPrefix: "পাদ ",
      retrograde: "বক্রী",
      exalted: "উচ্চ",
      debilitated: "নীচ",
      combust: "অস্ত",
      pushkaraBhaga: "পুষ্কর ভাগ",
      pushkaraD9: "পুষ্কর নবাংশ",
      pushkaraNoteTitle: "পুষ্কর নবাংশ / ভাগ:",
      pushkaraNote:
        "নিরয়ন রাশিচক্রের সেই শুভ অংশ, যা জাতক পারিজাত অনুসারে গ্রহকে পুষ্টি ও বল দেয়।",
      changesSignToday: "এই দিনে রাশি বদলায়",
      dayOnlyNote:
        "জন্ম সময় ছাড়া অংশ, নক্ষত্র ও ভাব দেখানো হয়নি: এগুলি এক মুহূর্তের হিসাব। উপরের রাশিগুলি আপনার জন্ম তারিখে সারা দিন একই থাকে।",
    },
    dasha: {
      vimshottari: "বিংশোত্তরী দশা",
      heading: "বর্তমান দশা ও অন্তর্দশা",
      intro:
        "মহাদশা জীবনের বড় সময়কাল; অন্তর্দশা তার ভেতরে চলা ছোট গ্রহচক্র। নিচের গ্রহ ও তারিখগুলি আপনার গণনা করা জন্ম কুষ্ঠি থেকে নেওয়া।",
      engine: "ইঞ্জিনে গণনা",
      activeNow: "এখন সক্রিয় গ্রহের সময়কাল",
      birthLord: (lord) =>
        `জন্মের সময়ের মহাদশা: ${lord}। এটি আপনার চন্দ্র নক্ষত্র থেকে নির্ধারিত, যা আপনার জন্ম তারিখে সারা দিন একই থাকে।`,
      orderPrefix: "মহাদশার ক্রম: ",
      datesHidden:
        "তারিখ দেখানো হয়নি: প্রথম মহাদশার অবশিষ্ট অংশ জন্মের মুহূর্তে চন্দ্রের সঠিক অংশের উপর নির্ভর করে, তাই প্রতিটি তারিখ — এবং এখন কোন দশা চলছে — জন্ম সময়ের উপর নির্ভর করে।",
      needsTime:
        "আপনার দশার জন্য জন্ম সময় দরকার: আপনার জন্ম তারিখে চন্দ্র নক্ষত্র বদলায়, আর নক্ষত্রই ঠিক করে আপনি কোন গ্রহের মহাদশায় জন্মেছেন — তাই ক্রম ও তারিখ দুটোই জন্ম সময়ের উপর নির্ভর করে।",
      rectify: "জীবনের ঘটনা থেকে জন্ম সময় অনুমান করুন",
      mahadasha: (lord) => `${lord} মহাদশা`,
      antardasha: (lord) => `${lord} অন্তর্দশা`,
    },
    slider: {
      ariaLabel: "বিংশোত্তরী দশার সময়রেখা",
      title: "বিংশোত্তরী মহাদশা ও অন্তর্দশা চক্র",
      subtitle: "120 বছরের দশা চক্রে আপনার বর্তমান মহাদশা ও অন্তর্দশা দেখুন",
      active: "সক্রিয় মহাদশা",
      years: "বছর",
      mahadashaHeading: (lord, years) => `${lord} মহাদশা (${years} বছর)`,
      antardashaHeading: "অন্তর্দশার উপ-কাল",
      now: "এখন",
    },
  },
  gu: {
    labels: {
      birthChart: "જન્મ કુંડળી",
      planetaryPositions: "ગ્રહ સ્થિતિ",
      doshaAnalysis: "દોષ વિશ્લેષણ",
      yogaAnalysis: "યોગ વિશ્લેષણ",
      yourReading: "તમારું ફળકથન",
      recommendedForYou: "તમારા માટે ભલામણ",
      moonSign: "ચંદ્ર રાશિ",
      nakshatra: "નક્ષત્ર",
      ascendant: "લગ્ન",
      doshasDetected: "મળ્યા",
      severity: "તીવ્રતા",
      recommended: "ભલામણ",
      shopNow: "હમણાં ખરીદો",
      shareYourKundali: "તમારી કુંડળી શેર કરો",
      shareDescription: "મિત્રો અને પરિવાર સાથે તમારું ફળકથન શેર કરો",
      shareOnWhatsApp: "WhatsApp પર શેર કરો",
      copyLink: "લિંક કૉપિ કરો",
      copied: "કૉપિ થયું!",
      newReading: "નવી કુંડળી",
      askAboutChart: "તમારી કુંડળી વિશે પૂછો",
      doshaWord: "દોષ",
      doshaWordPlural: "દોષ",
      kundali: "જન્મ કુંડળી",
    },
    title: (name) => `${name}ની જન્મ કુંડળી`,
    computedFor: "ગણતરી: ",
    timeNotSet: "સમય આપ્યો નથી",
    chartStyle: { north: "ઉત્તર ભારતીય (ડાયમંડ)", south: "દક્ષિણ ભારતીય (ચોરસ)" },
    southChart: {
      label: "દક્ષિણ ભારતીય કુંડળી",
      centerTitle: "દક્ષિણ ભારતીય કુંડળી",
      centerSubtitle: "સ્થિર રાશિચક્ર",
    },
    chartMethodNote:
      "લાહિરી અયનાંશ (નિરયન/sidereal) અને સંપૂર્ણ રાશિ (Whole-Sign) ભાવ પદ્ધતિથી ગણતરી — શાસ્ત્રીય વૈદિક (પારાશરી) પદ્ધતિ, જેમાં લગ્નની આખી રાશિ પહેલો ભાવ હોય છે. પશ્ચિમી (સાયન/tropical) કુંડળી કરતાં ગ્રહોની રાશિ સ્વાભાવિક રીતે અલગ હશે.",
    chartNeedsTime:
      "કુંડળીમાં ગ્રહોને લગ્નથી ગણેલા ભાવમાં મૂકવામાં આવે છે, અને લગ્ન માટે જન્મ સમય જોઈએ. આખો દિવસ એકસરખી રહેતી ગ્રહોની રાશિઓ નીચેના કોષ્ટકમાં છે.",
    doshaNeedsTime:
      "દોષની તપાસ માટે જન્મ સમય જોઈએ, કારણ કે આ તપાસ લગ્નથી ગણેલા ભાવનો ઉપયોગ કરે છે. જન્મ સમય વિના અમે કોઈ દોષ તમારો હોવાનું કહેતા નથી.",
    doshaPlacement: (graha, house) => `${graha}, ભાવ ${house}માં`,
    summary: {
      needsBirthTime: "જન્મ સમય જોઈએ",
      moonChangesRashi: "તમારી જન્મ તારીખે ચંદ્ર રાશિ બદલે છે",
      lagnaChanges: "લગભગ દર 2 કલાકે બદલાય છે",
      nakshatraChanges: "આ તારીખે બદલાય છે",
      pada: "ચરણ",
    },
    windows: {
      before: (time) => `${time} પહેલાં જન્મ`,
      after: (time) => `${time} કે પછી જન્મ`,
      between: (start, end) => `${start}–${end} વચ્ચે જન્મ`,
    },
    table: {
      title: "ગ્રહ સ્થિતિ",
      subtitle: "Planetary Positions",
      swipe: "સ્ક્રોલ કરો",
      columns: ["ગ્રહ", "રાશિ", "અંશ", "નક્ષત્ર", "ભાવ", "સ્થિતિ"],
      padaPrefix: "ચરણ ",
      retrograde: "વક્રી",
      exalted: "ઉચ્ચ",
      debilitated: "નીચ",
      combust: "અસ્ત",
      pushkaraBhaga: "પુષ્કર ભાગ",
      pushkaraD9: "પુષ્કર નવાંશ",
      pushkaraNoteTitle: "પુષ્કર નવાંશ / ભાગ:",
      pushkaraNote: "નિરયન રાશિચક્રના એવા શુભ અંશ, જે જાતક પારિજાત મુજબ ગ્રહને પોષણ અને બળ આપે છે.",
      changesSignToday: "આ દિવસે રાશિ બદલાય છે",
      dayOnlyNote:
        "જન્મ સમય વિના અંશ, નક્ષત્ર અને ભાવ બતાવ્યા નથી: તે એક ક્ષણ માટે જ નક્કી થાય છે. ઉપરની રાશિઓ તમારી જન્મ તારીખે આખો દિવસ એકસરખી રહે છે.",
    },
    dasha: {
      vimshottari: "વિંશોત્તરી દશા",
      heading: "વર્તમાન દશા અને અંતર્દશા",
      intro:
        "મહાદશા જીવનનો મોટો સમયગાળો બતાવે છે; અંતર્દશા તેની અંદર ચાલતું નાનું ગ્રહચક્ર છે. નીચેના ગ્રહો અને તારીખો તમારી ગણતરી કરેલી જન્મ કુંડળીમાંથી છે.",
      engine: "એન્જિન ગણતરી",
      activeNow: "હાલ સક્રિય ગ્રહ સમયગાળો",
      birthLord: (lord) =>
        `જન્મ સમયની મહાદશા: ${lord}. તે તમારા ચંદ્ર નક્ષત્રથી નક્કી થાય છે, જે તમારી જન્મ તારીખે આખો દિવસ એકસરખું રહે છે.`,
      orderPrefix: "મહાદશાનો ક્રમ: ",
      datesHidden:
        "તારીખો બતાવી નથી: પહેલી મહાદશાનો બાકી ભાગ જન્મ સમયે ચંદ્રના ચોક્કસ અંશ પર આધાર રાખે છે, તેથી દરેક તારીખ — અને હાલ કઈ દશા ચાલે છે — જન્મ સમય પર આધાર રાખે છે.",
      needsTime:
        "તમારી દશા માટે જન્મ સમય જોઈએ: તમારી જન્મ તારીખે ચંદ્ર નક્ષત્ર બદલે છે, અને નક્ષત્રથી જ નક્કી થાય છે કે તમે કયા ગ્રહની મહાદશામાં જન્મ્યા — તેથી ક્રમ અને તારીખો બંને જન્મ સમય પર આધાર રાખે છે.",
      rectify: "જીવનની ઘટનાઓ પરથી જન્મ સમયનો અંદાજ કાઢો",
      mahadasha: (lord) => `${lord} મહાદશા`,
      antardasha: (lord) => `${lord} અંતર્દશા`,
    },
    slider: {
      ariaLabel: "વિંશોત્તરી દશા સમયરેખા",
      title: "વિંશોત્તરી મહાદશા અને અંતર્દશા ચક્ર",
      subtitle: "120 વર્ષના દશા ચક્રમાં તમારી વર્તમાન મહાદશા અને અંતર્દશા જુઓ",
      active: "સક્રિય મહાદશા",
      years: "વર્ષ",
      mahadashaHeading: (lord, years) => `${lord} મહાદશા (${years} વર્ષ)`,
      antardashaHeading: "અંતર્દશા ઉપ-કાળ",
      now: "હાલ",
    },
  },
  mr: {
    labels: {
      birthChart: "जन्म कुंडली",
      planetaryPositions: "ग्रहस्थिती",
      doshaAnalysis: "दोष विश्लेषण",
      yogaAnalysis: "योग विश्लेषण",
      yourReading: "तुमचा फलादेश",
      recommendedForYou: "तुमच्यासाठी शिफारस",
      moonSign: "चंद्र रास",
      nakshatra: "नक्षत्र",
      ascendant: "लग्न",
      doshasDetected: "आढळले",
      severity: "तीव्रता",
      recommended: "शिफारस",
      shopNow: "आत्ता खरेदी करा",
      shareYourKundali: "तुमची कुंडली शेअर करा",
      shareDescription: "मित्र आणि कुटुंबासोबत तुमचा फलादेश शेअर करा",
      shareOnWhatsApp: "WhatsApp वर शेअर करा",
      copyLink: "लिंक कॉपी करा",
      copied: "कॉपी झाले!",
      newReading: "नवी कुंडली",
      askAboutChart: "तुमच्या कुंडलीबद्दल विचारा",
      doshaWord: "दोष",
      doshaWordPlural: "दोष",
      kundali: "जन्म कुंडली",
    },
    title: (name) => `${name} यांची जन्म कुंडली`,
    computedFor: "गणना: ",
    timeNotSet: "वेळ दिलेली नाही",
    chartStyle: { north: "उत्तर भारतीय (डायमंड)", south: "दक्षिण भारतीय (चौकोन)" },
    southChart: {
      label: "दक्षिण भारतीय कुंडली",
      centerTitle: "दक्षिण भारतीय कुंडली",
      centerSubtitle: "स्थिर राशीचक्र",
    },
    chartMethodNote:
      "लाहिरी अयनांश (निरयन/sidereal) आणि संपूर्ण राशी (Whole-Sign) भाव पद्धतीने गणना — शास्त्रीय वैदिक (पाराशरी) पद्धत, ज्यात लग्नाची संपूर्ण रास हा पहिला भाव असतो. पाश्चात्त्य (सायन/tropical) कुंडलीपेक्षा ग्रहांची रास स्वाभाविकपणे वेगळी असेल.",
    chartNeedsTime:
      "कुंडलीत ग्रह लग्नापासून मोजलेल्या भावांत मांडले जातात, आणि लग्नासाठी जन्मवेळ लागते. दिवसभर सारख्याच राहणाऱ्या ग्रहांच्या राशी खालील तक्त्यात आहेत.",
    doshaNeedsTime:
      "दोष तपासण्यासाठी जन्मवेळ लागते, कारण या तपासण्या लग्नापासून मोजलेले भाव वापरतात. जन्मवेळेशिवाय आम्ही कोणताही दोष तुमचा असल्याचे सांगत नाही.",
    doshaPlacement: (graha, house) => `${graha}, भाव ${house} मध्ये`,
    summary: {
      needsBirthTime: "जन्मवेळ आवश्यक",
      moonChangesRashi: "तुमच्या जन्मतारखेला चंद्र रास बदलतो",
      lagnaChanges: "साधारण दर 2 तासांनी बदलते",
      nakshatraChanges: "या तारखेला बदलते",
      pada: "चरण",
    },
    windows: {
      before: (time) => `${time} पूर्वी जन्म`,
      after: (time) => `${time} किंवा नंतर जन्म`,
      between: (start, end) => `${start}–${end} दरम्यान जन्म`,
    },
    table: {
      title: "ग्रहस्थिती",
      subtitle: "Planetary Positions",
      swipe: "स्क्रोल करा",
      columns: ["ग्रह", "रास", "अंश", "नक्षत्र", "भाव", "स्थिती"],
      padaPrefix: "चरण ",
      retrograde: "वक्री",
      exalted: "उच्च",
      debilitated: "नीच",
      combust: "अस्त",
      pushkaraBhaga: "पुष्कर भाग",
      pushkaraD9: "पुष्कर नवांश",
      pushkaraNoteTitle: "पुष्कर नवांश / भाग:",
      pushkaraNote:
        "निरयन राशीचक्रातील ते शुभ अंश, जे जातक पारिजातानुसार ग्रहाला पोषण आणि बळ देतात.",
      changesSignToday: "या दिवशी रास बदलते",
      dayOnlyNote:
        "जन्मवेळेशिवाय अंश, नक्षत्र आणि भाव दाखवलेले नाहीत: ते एका क्षणासाठीच ठरतात. वरील राशी तुमच्या जन्मतारखेला दिवसभर सारख्याच राहतात.",
    },
    dasha: {
      vimshottari: "विंशोत्तरी दशा",
      heading: "सध्याची दशा आणि अंतर्दशा",
      intro:
        "महादशा हा आयुष्याचा मोठा कालखंड आहे; अंतर्दशा हे त्यातील लहान ग्रहचक्र आहे. खालील ग्रह आणि तारखा तुमच्या गणना केलेल्या जन्म कुंडलीतून आल्या आहेत.",
      engine: "इंजिन गणना",
      activeNow: "सध्या सक्रिय ग्रह कालखंड",
      birthLord: (lord) =>
        `जन्मावेळची महादशा: ${lord}. ती तुमच्या चंद्र नक्षत्रावरून ठरते, जे तुमच्या जन्मतारखेला दिवसभर सारखेच राहते.`,
      orderPrefix: "महादशांचा क्रम: ",
      datesHidden:
        "तारखा दाखवलेल्या नाहीत: पहिल्या महादशेचा उरलेला भाग जन्मक्षणी चंद्राच्या अचूक अंशावर अवलंबून असतो, म्हणून प्रत्येक तारीख — आणि सध्या कोणती दशा सुरू आहे — जन्मवेळेवर अवलंबून असते.",
      needsTime:
        "तुमच्या दशेसाठी जन्मवेळ आवश्यक आहे: तुमच्या जन्मतारखेला चंद्र नक्षत्र बदलतो, आणि नक्षत्रावरूनच तुम्ही कोणत्या ग्रहाच्या महादशेत जन्मलात हे ठरते — म्हणून क्रम आणि तारखा दोन्ही जन्मवेळेवर अवलंबून आहेत.",
      rectify: "आयुष्यातील घटनांवरून जन्मवेळेचा अंदाज लावा",
      mahadasha: (lord) => `${lord} महादशा`,
      antardasha: (lord) => `${lord} अंतर्दशा`,
    },
    slider: {
      ariaLabel: "विंशोत्तरी दशा कालरेषा",
      title: "विंशोत्तरी महादशा आणि अंतर्दशा चक्र",
      subtitle: "120 वर्षांच्या दशा चक्रात तुमची सध्याची महादशा आणि अंतर्दशा पाहा",
      active: "सक्रिय महादशा",
      years: "वर्षे",
      mahadashaHeading: (lord, years) => `${lord} महादशा (${years} वर्षे)`,
      antardashaHeading: "अंतर्दशा उप-कालखंड",
      now: "सध्या",
    },
  },
  or: {
    labels: {
      birthChart: "ଜନ୍ମ କୁଣ୍ଡଳୀ",
      planetaryPositions: "ଗ୍ରହ ସ୍ଥିତି",
      doshaAnalysis: "ଦୋଷ ବିଶ୍ଳେଷଣ",
      yogaAnalysis: "ଯୋଗ ବିଶ୍ଳେଷଣ",
      yourReading: "ଆପଣଙ୍କ ଫଳାଦେଶ",
      recommendedForYou: "ଆପଣଙ୍କ ପାଇଁ ପରାମର୍ଶ",
      moonSign: "ଚନ୍ଦ୍ର ରାଶି",
      nakshatra: "ନକ୍ଷତ୍ର",
      ascendant: "ଲଗ୍ନ",
      doshasDetected: "ମିଳିଲା",
      severity: "ତୀବ୍ରତା",
      recommended: "ପରାମର୍ଶିତ",
      shopNow: "ଏବେ କିଣନ୍ତୁ",
      shareYourKundali: "ଆପଣଙ୍କ କୁଣ୍ଡଳୀ ସେୟାର୍ କରନ୍ତୁ",
      shareDescription: "ସାଙ୍ଗ ଓ ପରିବାର ସହ ଆପଣଙ୍କ ଫଳାଦେଶ ସେୟାର୍ କରନ୍ତୁ",
      shareOnWhatsApp: "WhatsApp ରେ ସେୟାର୍ କରନ୍ତୁ",
      copyLink: "ଲିଙ୍କ୍ କପି କରନ୍ତୁ",
      copied: "କପି ହେଲା!",
      newReading: "ନୂଆ କୁଣ୍ଡଳୀ",
      askAboutChart: "ଆପଣଙ୍କ କୁଣ୍ଡଳୀ ବିଷୟରେ ପଚାରନ୍ତୁ",
      doshaWord: "ଦୋଷ",
      doshaWordPlural: "ଦୋଷ",
      kundali: "ଜନ୍ମ କୁଣ୍ଡଳୀ",
    },
    title: (name) => `${name}ଙ୍କ ଜନ୍ମ କୁଣ୍ଡଳୀ`,
    computedFor: "ଗଣନା: ",
    timeNotSet: "ସମୟ ଦିଆଯାଇନାହିଁ",
    chartStyle: { north: "ଉତ୍ତର ଭାରତୀୟ (ଡାଇମଣ୍ଡ)", south: "ଦକ୍ଷିଣ ଭାରତୀୟ (ବର୍ଗାକାର)" },
    southChart: {
      label: "ଦକ୍ଷିଣ ଭାରତୀୟ କୁଣ୍ଡଳୀ",
      centerTitle: "ଦକ୍ଷିଣ ଭାରତୀୟ କୁଣ୍ଡଳୀ",
      centerSubtitle: "ସ୍ଥିର ରାଶିଚକ୍ର",
    },
    chartMethodNote:
      "ଲାହିଡ଼ୀ ଅୟନାଂଶ (ନିରୟଣ/sidereal) ଓ ପୂର୍ଣ୍ଣ ରାଶି (Whole-Sign) ଭାବ ପଦ୍ଧତିରେ ଗଣନା — ଶାସ୍ତ୍ରୀୟ ବୈଦିକ (ପାରାଶରୀ) ପଦ୍ଧତି, ଯେଉଁଥିରେ ଲଗ୍ନର ପୂରା ରାଶି ପ୍ରଥମ ଭାବ। ପାଶ୍ଚାତ୍ୟ (ସାୟନ/tropical) କୁଣ୍ଡଳୀଠାରୁ ଗ୍ରହମାନଙ୍କ ରାଶି ସ୍ୱାଭାବିକ ଭାବେ ଅଲଗା ହେବ।",
    chartNeedsTime:
      "କୁଣ୍ଡଳୀରେ ଗ୍ରହମାନଙ୍କୁ ଲଗ୍ନଠାରୁ ଗଣାଯାଇଥିବା ଭାବରେ ରଖାଯାଏ, ଏବଂ ଲଗ୍ନ ପାଇଁ ଜନ୍ମ ସମୟ ଆବଶ୍ୟକ। ଦିନସାରା ସମାନ ରହୁଥିବା ଗ୍ରହମାନଙ୍କ ରାଶି ତଳ ସାରଣୀରେ ଅଛି।",
    doshaNeedsTime:
      "ଦୋଷ ଯାଞ୍ଚ ପାଇଁ ଜନ୍ମ ସମୟ ଆବଶ୍ୟକ, କାରଣ ଏହି ଯାଞ୍ଚ ଲଗ୍ନଠାରୁ ଗଣାଯାଇଥିବା ଭାବ ବ୍ୟବହାର କରେ। ଜନ୍ମ ସମୟ ବିନା ଆମେ କୌଣସି ଦୋଷକୁ ଆପଣଙ୍କର ବୋଲି କହୁନାହୁଁ।",
    doshaPlacement: (graha, house) => `${graha} ${house} ନମ୍ବର ଭାବରେ`,
    summary: {
      needsBirthTime: "ଜନ୍ମ ସମୟ ଆବଶ୍ୟକ",
      moonChangesRashi: "ଆପଣଙ୍କ ଜନ୍ମ ତାରିଖରେ ଚନ୍ଦ୍ର ରାଶି ବଦଳାଏ",
      lagnaChanges: "ପ୍ରାୟ ପ୍ରତି 2 ଘଣ୍ଟାରେ ବଦଳେ",
      nakshatraChanges: "ଏହି ତାରିଖରେ ବଦଳେ",
      pada: "ପାଦ",
    },
    windows: {
      before: (time) => `${time} ପୂର୍ବରୁ ଜନ୍ମ`,
      after: (time) => `${time} ବା ତା' ପରେ ଜନ୍ମ`,
      between: (start, end) => `${start}–${end} ମଧ୍ୟରେ ଜନ୍ମ`,
    },
    table: {
      title: "ଗ୍ରହ ସ୍ଥିତି",
      subtitle: "Planetary Positions",
      swipe: "ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ",
      columns: ["ଗ୍ରହ", "ରାଶି", "ଅଂଶ", "ନକ୍ଷତ୍ର", "ଭାବ", "ସ୍ଥିତି"],
      padaPrefix: "ପାଦ ",
      retrograde: "ବକ୍ରୀ",
      exalted: "ଉଚ୍ଚ",
      debilitated: "ନୀଚ",
      combust: "ଅସ୍ତ",
      pushkaraBhaga: "ପୁଷ୍କର ଭାଗ",
      pushkaraD9: "ପୁଷ୍କର ନବାଂଶ",
      pushkaraNoteTitle: "ପୁଷ୍କର ନବାଂଶ / ଭାଗ:",
      pushkaraNote: "ନିରୟଣ ରାଶିଚକ୍ରର ସେହି ଶୁଭ ଅଂଶ, ଯାହା ଜାତକ ପାରିଜାତ ଅନୁସାରେ ଗ୍ରହକୁ ପୋଷଣ ଓ ବଳ ଦିଏ।",
      changesSignToday: "ଏହି ଦିନ ରାଶି ବଦଳେ",
      dayOnlyNote:
        "ଜନ୍ମ ସମୟ ବିନା ଅଂଶ, ନକ୍ଷତ୍ର ଓ ଭାବ ଦେଖାଯାଇନାହିଁ: ଏଗୁଡ଼ିକ ଗୋଟିଏ ମୁହୂର୍ତ୍ତର ହିସାବ। ଉପରର ରାଶିଗୁଡ଼ିକ ଆପଣଙ୍କ ଜନ୍ମ ତାରିଖରେ ଦିନସାରା ସମାନ ରହେ।",
    },
    dasha: {
      vimshottari: "ବିଂଶୋତ୍ତରୀ ଦଶା",
      heading: "ବର୍ତ୍ତମାନର ଦଶା ଓ ଅନ୍ତର୍ଦଶା",
      intro:
        "ମହାଦଶା ଜୀବନର ବଡ଼ ସମୟକାଳ; ଅନ୍ତର୍ଦଶା ତା' ଭିତରେ ଚାଲୁଥିବା ଛୋଟ ଗ୍ରହଚକ୍ର। ତଳେ ଥିବା ଗ୍ରହ ଓ ତାରିଖ ଆପଣଙ୍କ ଗଣନା କରାଯାଇଥିବା ଜନ୍ମ କୁଣ୍ଡଳୀରୁ ଆସିଛି।",
      engine: "ଇଞ୍ଜିନ୍ ଗଣନା",
      activeNow: "ଏବେ ସକ୍ରିୟ ଗ୍ରହ ସମୟକାଳ",
      birthLord: (lord) =>
        `ଜନ୍ମ ସମୟର ମହାଦଶା: ${lord}। ଏହା ଆପଣଙ୍କ ଚନ୍ଦ୍ର ନକ୍ଷତ୍ରରୁ ସ୍ଥିର ହୁଏ, ଯାହା ଆପଣଙ୍କ ଜନ୍ମ ତାରିଖରେ ଦିନସାରା ସମାନ ରହେ।`,
      orderPrefix: "ମହାଦଶାର କ୍ରମ: ",
      datesHidden:
        "ତାରିଖ ଦେଖାଯାଇନାହିଁ: ପ୍ରଥମ ମହାଦଶାର ବାକି ଅଂଶ ଜନ୍ମ ମୁହୂର୍ତ୍ତରେ ଚନ୍ଦ୍ରର ସଠିକ୍ ଅଂଶ ଉପରେ ନିର୍ଭର କରେ, ତେଣୁ ପ୍ରତ୍ୟେକ ତାରିଖ — ଏବଂ ଏବେ କେଉଁ ଦଶା ଚାଲିଛି — ଜନ୍ମ ସମୟ ଉପରେ ନିର୍ଭର କରେ।",
      needsTime:
        "ଆପଣଙ୍କ ଦଶା ପାଇଁ ଜନ୍ମ ସମୟ ଆବଶ୍ୟକ: ଆପଣଙ୍କ ଜନ୍ମ ତାରିଖରେ ଚନ୍ଦ୍ର ନକ୍ଷତ୍ର ବଦଳାଏ, ଏବଂ ନକ୍ଷତ୍ର ହିଁ ସ୍ଥିର କରେ ଆପଣ କେଉଁ ଗ୍ରହର ମହାଦଶାରେ ଜନ୍ମ ହୋଇଛନ୍ତି — ତେଣୁ କ୍ରମ ଓ ତାରିଖ ଦୁଇଟି ଜନ୍ମ ସମୟ ଉପରେ ନିର୍ଭର କରେ।",
      rectify: "ଜୀବନର ଘଟଣାରୁ ଜନ୍ମ ସମୟ ଅନୁମାନ କରନ୍ତୁ",
      mahadasha: (lord) => `${lord} ମହାଦଶା`,
      antardasha: (lord) => `${lord} ଅନ୍ତର୍ଦଶା`,
    },
    slider: {
      ariaLabel: "ବିଂଶୋତ୍ତରୀ ଦଶା ସମୟରେଖା",
      title: "ବିଂଶୋତ୍ତରୀ ମହାଦଶା ଓ ଅନ୍ତର୍ଦଶା ଚକ୍ର",
      subtitle: "120 ବର୍ଷର ଦଶା ଚକ୍ରରେ ଆପଣଙ୍କ ବର୍ତ୍ତମାନର ମହାଦଶା ଓ ଅନ୍ତର୍ଦଶା ଦେଖନ୍ତୁ",
      active: "ସକ୍ରିୟ ମହାଦଶା",
      years: "ବର୍ଷ",
      mahadashaHeading: (lord, years) => `${lord} ମହାଦଶା (${years} ବର୍ଷ)`,
      antardashaHeading: "ଅନ୍ତର୍ଦଶା ଉପ-କାଳ",
      now: "ଏବେ",
    },
  },
  ta: {
    labels: {
      birthChart: "ஜாதகம்",
      planetaryPositions: "கிரக நிலைகள்",
      doshaAnalysis: "தோஷ ஆய்வு",
      yogaAnalysis: "யோக ஆய்வு",
      yourReading: "உங்கள் பலன்",
      recommendedForYou: "உங்களுக்கான பரிந்துரை",
      moonSign: "சந்திர ராசி",
      nakshatra: "நட்சத்திரம்",
      ascendant: "லக்னம்",
      doshasDetected: "கண்டறியப்பட்டன",
      severity: "தீவிரம்",
      recommended: "பரிந்துரை",
      shopNow: "இப்போதே வாங்கவும்",
      shareYourKundali: "உங்கள் ஜாதகத்தைப் பகிரவும்",
      shareDescription: "உங்கள் பலனை நண்பர்கள் மற்றும் குடும்பத்தினருடன் பகிரவும்",
      shareOnWhatsApp: "WhatsApp-இல் பகிரவும்",
      copyLink: "இணைப்பை நகலெடுக்கவும்",
      copied: "நகலெடுக்கப்பட்டது!",
      newReading: "புதிய ஜாதகம்",
      askAboutChart: "உங்கள் ஜாதகம் பற்றிக் கேளுங்கள்",
      doshaWord: "தோஷம்",
      doshaWordPlural: "தோஷங்கள்",
      kundali: "ஜாதகம்",
    },
    title: (name) => `${name} அவர்களின் ஜாதகம்`,
    computedFor: "கணிப்பு: ",
    timeNotSet: "நேரம் தரப்படவில்லை",
    chartStyle: { north: "வட இந்தியம் (வைரம்)", south: "தென் இந்தியம் (சதுரம்)" },
    southChart: {
      label: "தென் இந்திய ராசிக் கட்டம்",
      centerTitle: "ராசிக் கட்டம்",
      centerSubtitle: "நிலையான ராசி மண்டலம்",
    },
    chartMethodNote:
      "லாஹிரி அயனாம்சம் (நிராயன/sidereal) மற்றும் முழு ராசி (Whole-Sign) பாவ முறையில் கணிக்கப்பட்டது — லக்னத்தின் முழு ராசியே முதல் பாவமாகும் பாரம்பரிய வேத (பராசர) முறை. மேற்கத்திய (சாயன/tropical) ஜாதகத்திலிருந்து கிரகங்களின் ராசி இயல்பாகவே வேறுபடும்.",
    chartNeedsTime:
      "ஜாதகக் கட்டத்தில் கிரகங்கள் லக்னத்திலிருந்து எண்ணப்படும் பாவங்களில் வைக்கப்படுகின்றன; லக்னத்திற்குப் பிறந்த நேரம் தேவை. நாள் முழுவதும் மாறாத கிரக ராசிகள் கீழே உள்ள அட்டவணையில் உள்ளன.",
    doshaNeedsTime:
      "தோஷ ஆய்வுக்குப் பிறந்த நேரம் தேவை, ஏனெனில் இந்த ஆய்வுகள் லக்னத்திலிருந்து எண்ணப்படும் பாவங்களைப் பயன்படுத்துகின்றன. பிறந்த நேரம் இல்லாமல் எந்தத் தோஷத்தையும் உங்களுடையது என்று நாங்கள் கூறுவதில்லை.",
    doshaPlacement: (graha, house) => `${graha} ${house}-ஆம் பாவத்தில்`,
    summary: {
      needsBirthTime: "பிறந்த நேரம் தேவை",
      moonChangesRashi: "உங்கள் பிறந்த தேதியில் சந்திரன் ராசி மாறுகிறது",
      lagnaChanges: "சுமார் 2 மணி நேரத்திற்கு ஒருமுறை மாறும்",
      nakshatraChanges: "இந்தத் தேதியில் மாறுகிறது",
      pada: "பாதம்",
    },
    windows: {
      before: (time) => `${time}-க்கு முன் பிறந்தவர்`,
      after: (time) => `${time} அல்லது அதற்குப் பின் பிறந்தவர்`,
      between: (start, end) => `${start}–${end} இடையே பிறந்தவர்`,
    },
    table: {
      title: "கிரக நிலைகள்",
      subtitle: "Planetary Positions",
      swipe: "நகர்த்தவும்",
      columns: ["கிரகம்", "ராசி", "பாகை", "நட்சத்திரம்", "பாவம்", "நிலை"],
      padaPrefix: "பாதம் ",
      retrograde: "வக்கிரம்",
      exalted: "உச்சம்",
      debilitated: "நீசம்",
      combust: "அஸ்தங்கம்",
      pushkaraBhaga: "புஷ்கர பாகம்",
      pushkaraD9: "புஷ்கர நவாம்சம்",
      pushkaraNoteTitle: "புஷ்கர நவாம்சம் / பாகம்:",
      pushkaraNote:
        "ஜாதக பாரிஜாதத்தின்படி கிரகத்திற்கு வலிமையும் ஊட்டமும் தரும் நிராயன ராசி மண்டலத்தின் சுப பாகைகள்.",
      changesSignToday: "இந்த நாளில் ராசி மாறுகிறது",
      dayOnlyNote:
        "பிறந்த நேரம் இல்லாததால் பாகை, நட்சத்திரம், பாவம் காட்டப்படவில்லை: இவை ஒரு கணத்திற்கு உரியவை. மேலே உள்ள ராசிகள் உங்கள் பிறந்த தேதியில் நாள் முழுவதும் மாறாதவை.",
    },
    dasha: {
      vimshottari: "விம்சோத்தரி தசை",
      heading: "நடப்பு தசை மற்றும் புக்தி",
      intro:
        "மகா தசை வாழ்க்கையின் பெரிய காலகட்டம்; புக்தி அதற்குள் நடக்கும் சிறிய கிரகக் காலம். கீழே உள்ள கிரகங்களும் தேதிகளும் உங்கள் கணிக்கப்பட்ட ஜாதகத்திலிருந்து வந்தவை.",
      engine: "இயந்திரக் கணிப்பு",
      activeNow: "இப்போது நடக்கும் கிரகக் காலங்கள்",
      birthLord: (lord) =>
        `பிறக்கும்போது நடந்த மகா தசை: ${lord}. இது உங்கள் சந்திர நட்சத்திரத்தால் தீர்மானிக்கப்படுகிறது; அது உங்கள் பிறந்த தேதியில் நாள் முழுவதும் மாறாது.`,
      orderPrefix: "மகா தசைகளின் வரிசை: ",
      datesHidden:
        "தேதிகள் காட்டப்படவில்லை: முதல் மகா தசையின் மீதி, பிறந்த நேரத்தில் சந்திரனின் துல்லியமான பாகையைப் பொறுத்தது; எனவே ஒவ்வொரு தேதியும் — இப்போது எந்தத் தசை நடக்கிறது என்பதும் — பிறந்த நேரத்தைப் பொறுத்தது.",
      needsTime:
        "உங்கள் தசைக்குப் பிறந்த நேரம் தேவை: உங்கள் பிறந்த தேதியில் சந்திரன் நட்சத்திரம் மாறுகிறது, நட்சத்திரமே நீங்கள் எந்தக் கிரகத்தின் மகா தசையில் பிறந்தீர்கள் என்பதைத் தீர்மானிக்கிறது — எனவே வரிசையும் தேதிகளும் பிறந்த நேரத்தைப் பொறுத்தவை.",
      rectify: "வாழ்க்கை நிகழ்வுகளிலிருந்து பிறந்த நேரத்தைக் கணிக்கவும்",
      mahadasha: (lord) => `${lord} மகா தசை`,
      antardasha: (lord) => `${lord} புக்தி`,
    },
    slider: {
      ariaLabel: "விம்சோத்தரி தசை காலவரிசை",
      title: "விம்சோத்தரி மகா தசை மற்றும் புக்தி சக்கரம்",
      subtitle: "120 ஆண்டு தசை சக்கரத்தில் உங்கள் நடப்பு மகா தசையையும் புக்தியையும் பாருங்கள்",
      active: "நடப்பு மகா தசை",
      years: "ஆண்டுகள்",
      mahadashaHeading: (lord, years) => `${lord} மகா தசை (${years} ஆண்டுகள்)`,
      antardashaHeading: "புக்திக் காலங்கள்",
      now: "இப்போது",
    },
  },
};

/** The regional result copy for `language`, or null for en / hi / anything else. */
export function regionalResultCopy(language: string | null | undefined): RegionalResultCopy | null {
  const locale = kundaliRegionalLocale(language);
  return locale ? REGIONAL_RESULT_COPY[locale] : null;
}
